import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import LoaderComponent from '@components/LoaderComponent';
import NoData from '@components/NoData';
import OrderLists from '@components/OrderLists';
import { toastError } from '@components/Toast';

import useDocumentTitle from '@hooks/useDocumentTitle';

import {
	allFilterPages,
	generateOrderDashboardParams,
	generateTeamParams,
	orderDashboardPageList,
	orderPageFieldList
} from '@features/filter/static/filterUtil';
import FilterComponentV2 from '@features/filter/v2/FilterComponent';
import MyOrderTabs from '@features/order/MyOrderTabs';
import FilterTopHeader from '@features/order-dashboard/component/FilterTopHeader';
import OrderDashboard from '@features/order-dashboard/OrderDashboard';
import OrderReportingSideBar from '@features/order-intel/OrderReportingSideBar';

import { clearGlobalFilter, storeInitialItemInGlobalFilter } from '@store/actions/FilterAction';
import { fetchOrderDashboardHeadings } from '@store/actions/OrderDashboardAction';

import { getOrderByOrderDashboardId, getOrderDashboardFilterOptions } from '@services/orderDashboard';

import ApiClient from '@lib/apiClient';

import { encodeQueryData, getUserPreference, isPageReachBottom, printApiError } from '@utils/Util';

import {
	APPROVAL_REQUEST_STATUS_APPROVED,
	APPROVAL_REQUEST_STATUS_PENDING,
	APPROVAL_REQUEST_TYPE_CANCEL,
	RFQ_CHANGE_APPROVAL_TYPES
} from '@/constants/constant';

import { ReactComponent as AiMagicIcon } from '@/assets/images/ai-model/ai-magic.svg';
import { getAccountManagers, getBrandList, getProjectManagers } from '@/store/filter/action';
import { Types } from '@/store/types';
import { fetchEnumValues, setSystemUserPreferences } from '@/services/commonService';

const tabFilterMap = {
	PENDING: 'PENDING',
	RUNNING: 'RUNNING',
	COMPLETED: 'COMPLETED',
	ORDER_DASHBOARD: 'ORDER_DASHBOARD',
	ON_SHIPMENT: 'ON_SHIPMENT',
	APPROVAL: 'APPROVAL'
};
const entryState = [
	{
		id: 'true',
		name: 'Draft Order',
		value: 'YES'
	},
	{
		id: 'false',
		name: 'Placed Order',
		value: 'NO'
	}
];

const MyOrder = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch = useDispatch();
	const myTeamMembers = useSelector((store) => store.teamMembersReducer);
	const orderDashboardFilterFields = useSelector((store) => store.filterFieldStore);
	const userPreference = getUserPreference();
	const { brandList, accountManagerList, projectManagerList } = useSelector((store) => store.filters);
	const [state, setState] = useState({
		projectList: [],
		page: 0,
		size: 15,
		loading: false,
		activeTab: '',
		isPendingTab: false,
		filterBy: '',
		sort: ['RUNNING', 'PENDING', 'COMPLETED', 'ON_SHIPMENT', 'APPROVAL'],
		project_type: ['BULK', 'DEVELOPMENT', 'SUPERVISION'],
		hasNext: false,
		height: window.innerHeight,
		userInfo: {},
		runningCount: 0,
		pendingCount: 0,
		completedCount: 0,
		cancelCount: 0,
		shipmentCount: 0,
		search: '',
		isSearching: false,
		totalElements: 0,
		showFilter: false,
		showAskModal: false,
		showSummary:
			userPreference?.orderDashboardShowSummary !== undefined
				? userPreference?.orderDashboardShowSummary
				: true,
		queryParams: '',
		revenueStream: [],
		approvalRequestType: '',
		approvalRequestStatus: APPROVAL_REQUEST_STATUS_PENDING
	});
	useEffect(() => {
		(async () => {
			try {
				const salesFunnels = await fetchEnumValues('info.nitex.app.common.enums.OrderStream');
				setState((prevState) => ({
					...prevState,
					revenueStream: salesFunnels?.map((item) => ({
						id: item.constant,
						name: item.name
					}))
				}));
			} catch {}
		})();
	}, []);

	useEffect(() => {
		Promise.all([
			dispatch(getBrandList()),
			dispatch(getAccountManagers(), dispatch(getProjectManagers()))
		]);
	}, []);

	useEffect(() => {
		if (state.activeTab !== 'ORDER_DASHBOARD') {
			const filterList = {
				brandList: brandList,
				accountManagerList: accountManagerList,
				projectManagerList: projectManagerList,
				streamList: state.revenueStream,
				isDraftedList: entryState
			};
			dispatch({
				type: Types.STORE_DEPENDENT_FILTER_LIST,
				payload: { responseData: filterList, filterListPage: 'MY_ORDER' }
			});
		}
	}, [brandList, accountManagerList, projectManagerList, state.activeTab]);

	useEffect(() => {
		if (state.activeTab === 'ORDER_DASHBOARD' && state.showFilter) {
			(async () => {
				let orderQuery = generateOrderDashboardParams(orderDashboardFilterFields);
				let params = orderQuery.slice(1);
				const data = await getOrderDashboardFilterOptions(`?${params}`);
				const filterList = {
					brandList: data?.brandList || [],
					accountManagerList: data?.accountManagerList || [],
					projectManagerList: data?.projectManagerList || [],
					streamList: (data?.revenueStreamList || [])?.map((item) => ({
						id: item.constant,
						name: item.name
					})),
					commercialInvoiceList: data?.commercialInvoiceList || [],
					fabricTypeList: data?.fabricTypeList || [],
					tnaStatusList: data?.tnaStatusList || [],
					salesContractList: data?.salesContractList || [],
					orderList: data?.orderList || [],
					poNumberList: data?.poNumberList || [],
					marketList: data?.marketList || [],
					subCategoryList: data?.subCategoryList || [],
					factoryList: data?.factoryList || [],
					orderStatusList: data?.orderStatusList || [],
					orderMemberList: data?.orderMemberList || [],
					designByList: data?.desingByList || [],
					programTypeList: data?.programTypeList || []
				};
				dispatch({
					type: Types.STORE_DEPENDENT_FILTER_LIST,
					payload: { responseData: filterList, filterListPage: 'MY_ORDER' }
				});
			})();
		}
	}, [state.activeTab, orderDashboardFilterFields, state.showFilter]);

	useDocumentTitle('My orders with Nitex - The easiest clothing manufacturing software');

	const handleScroll = async () => {
		if (isPageReachBottom()) {
			let { hasNext, page, loading } = state;
			if (hasNext && !loading) {
				await renderList(page + 1, state.activeTab, true);
			}
		}
	};

	const handleOrderDashboardScroll = async () => {
		const wrappedElement = document.getElementById('orderDashboardTable');
		if (
			Math.abs(
				Math.ceil(wrappedElement.scrollHeight - wrappedElement.scrollTop) -
					wrappedElement.clientHeight
			) <= 5
		) {
			let { hasNext, page, loading } = state;
			if (hasNext && !loading) {
				await renderList(page + 1, state.activeTab, true);
			}
		}
	};

	const getTabWiseOrders = async (tabName) => {
		setState((prevState) => ({
			...prevState,
			activeTab: tabName,
			isPendingTab: false,
			approvalRequestType: '',
			approvalRequestStatus: APPROVAL_REQUEST_STATUS_PENDING
		}));
		navigate(`/orders/my-orders?active=${tabName}`);
	};

	const getOrderDashboardReports = async (tab, query) => {
		let isRequestFromCRM = query?.get('requestFrom') === 'CRM';
		// Grouping by TNA status is no longer one of the sort options, so every role opens on
		// grouping by order rather than on a grouping the dropdown cannot show.
		const tmpFilterFields = {
			sort: [
				{
					name: 'sort',
					type: 'text',
					key: 'order.id,desc',
					value: 'Group by order',
					identifier: 'sort-order'
				}
			]
		};

		if (!isRequestFromCRM) {
			tmpFilterFields['orderStatusList'] = [
				{
					name: 'orderStatusList',
					key: 'ON_SHIPMENT',
					value: 'on shipment',
					identifier: 'orderStatusList_ON_SHIPMENT',
					checked: true
				},
				{
					name: 'orderStatusList',
					key: 'RUNNING',
					value: 'running',
					identifier: 'orderStatusList_RUNNING',
					checked: true
				}
			];
		} else {
			if (query.get('orderId')) {
				tmpFilterFields['orderIdList'] = [
					{
						name: 'orderIdList',
						key: query.get('orderId'),
						value: query.get('orderName'),
						identifier: `orderIdList_${query.get('orderName')}`,
						checked: true
					}
				];
			}
			if (query.get('brands')) {
				tmpFilterFields['orderIdList'] = [
					{
						name: 'brandIdList',
						key: query.get('brandId'),
						value: query.get('brandName'),
						identifier: `brandIdList_${query.get('brandId')}`,
						checked: true
					}
				];
			}
			if (query.get('search')) {
				tmpFilterFields['orderIdList'] = [
					{
						name: 'name',
						type: 'text',
						key: query.get('search'),
						value: query.get('search'),
						identifier: 'name',
						isHideChip: false
					}
				];
			}
		}

		await dispatch(storeInitialItemInGlobalFilter(tmpFilterFields));
		setState((prevState) => ({ ...prevState, activeTab: 'ORDER_DASHBOARD', isPendingTab: false }));
		navigate(`/orders/my-orders?active=${tab}`);
	};

	useEffect(() => {
		(async () => {
			await renderList(0, state.activeTab, false);
		})();
	}, [orderDashboardFilterFields, myTeamMembers]);

	const getSelectedTabData = async (tab, isInitialRender, query) => {
		if (tab === 'ORDER_DASHBOARD') {
			await dispatch(fetchOrderDashboardHeadings());
			await getOrderDashboardReports(tab, query);
		} else {
			await getTabWiseOrders(tab, isInitialRender);
			await dispatch(clearGlobalFilter());
		}
	};

	const changeTab = async (tabName) => {
		window.scrollTo(0, 0);
		await getSelectedTabData(tabName, true);
	};

	useEffect(() => {
		(async () => {
			const query = await new URLSearchParams(location.search);
			const previousActiveTab = query.get('active');
			await getSelectedTabData(previousActiveTab, true, query);
			let userInfo = localStorage.getItem('userInfo');
			setState((prevState) => ({
				...prevState,
				userInfo: userInfo ? JSON.parse(userInfo) : {}
			}));
			await getStatusCount();
		})();
	}, []);

	useEffect(() => {
		window.addEventListener('scroll', handleScroll);

		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
	}, [state]);

	const getStatusCount = async () => {
		await ApiClient.GET('getStatusCount')
			.then(async ({ data }) => {
				if (data) {
					let etdApprovalCount = 0;
					try {
						const { data: etdCount } = await ApiClient.GET('rfqChangeApprovalRequestCount');
						etdApprovalCount = Number(etdCount) || 0;
					} catch {
						etdApprovalCount = 0;
					}
					setState((prevState) => ({
						...prevState,
						pendingCount: data.PENDING,
						runningCount: data.RUNNING,
						completedCount: data.COMPLETED,
						cancelCount: (data.REQUESTED || 0) + etdApprovalCount,
						shipmentCount: data.ON_SHIPMENT
					}));
				}
			})
			.catch((error) => {
				printApiError(error);
			});
	};

	const mergeRfqChangeRequestsByOrder = (requests) => {
		const orderWiseRequestMap = new Map();
		requests.forEach((request) => {
			const key =
				request?.orderId != null
					? `${request?.approvalType}_${request.orderId}`
					: `request_${request?.id}`;
			const existing = orderWiseRequestMap.get(key);
			if (!existing) {
				orderWiseRequestMap.set(key, {
					...request,
					pendingStyleCount: 1,
					requestedByList: request?.requestedBy ? [request.requestedBy] : []
				});
				return;
			}
			const pendingStyleCount = existing.pendingStyleCount + 1;
			const requestedByList =
				request?.requestedBy && !existing.requestedByList.includes(request.requestedBy)
					? [...existing.requestedByList, request.requestedBy]
					: existing.requestedByList;
			const existingTime = new Date(existing.requestedAt).getTime() || 0;
			const currentTime = new Date(request?.requestedAt).getTime() || 0;
			const latest = currentTime > existingTime ? request : existing;
			orderWiseRequestMap.set(key, {
				...latest,
				pendingStyleCount,
				requestedByList,
				requestedBy: requestedByList.join(', ')
			});
		});
		return Array.from(orderWiseRequestMap.values());
	};

	const dropAlreadyListedRfqChangeOrders = (incoming, existingList) => {
		const listedKeys = new Set(
			existingList
				.filter((item) => RFQ_CHANGE_APPROVAL_TYPES.includes(item?.approvalType) && item?.orderId)
				.map((item) => `${item.approvalType}_${item.orderId}`)
		);
		return incoming.filter(
			(item) =>
				!RFQ_CHANGE_APPROVAL_TYPES.includes(item?.approvalType) ||
				!listedKeys.has(`${item.approvalType}_${item.orderId}`)
		);
	};

	const renderApprovalList = async (
		page = 0,
		merge = true,
		requestType = state.approvalRequestType,
		requestStatus = state.approvalRequestStatus
	) => {
		setState((prevState) => ({ ...prevState, loading: true }));
		const { size, projectList } = state;

		const isCancelRequested = requestType === APPROVAL_REQUEST_TYPE_CANCEL;
		const includeCancel = !requestType || isCancelRequested;
		const includeRfqChange = !requestType || !isCancelRequested;
		const isApproved = requestStatus === APPROVAL_REQUEST_STATUS_APPROVED;

		const cancelParams = new URLSearchParams({
			page,
			size,
			sort: 'id,desc',
			status: isApproved ? APPROVAL_REQUEST_STATUS_APPROVED : 'REQUESTED'
		});
		const etdParams = new URLSearchParams({
			page,
			size,
			sort: 'id,desc',
			status: isApproved ? APPROVAL_REQUEST_STATUS_APPROVED : APPROVAL_REQUEST_STATUS_PENDING
		});
		if (includeRfqChange && requestType) etdParams.set('changeField', requestType);

		try {
			const [cancelRes, etdRes] = await Promise.all([
				includeCancel ? ApiClient.GET('orderCancelRequest', `?${cancelParams.toString()}`) : null,
				includeRfqChange
					? ApiClient.GET('rfqChangeApprovalRequests', `?${etdParams.toString()}`)
					: null
			]);

			const cancelData = cancelRes?.data?.data || [];
			const etdData = etdRes?.data?.data || [];
			// Multiple styles of the same order can each raise an ETD or factory price change request.
			// Show one card per order and request type; the details page lists every pending style.
			const mergedEtdData = mergeRfqChangeRequestsByOrder(etdData);
			const combined = [...cancelData, ...mergedEtdData].sort((a, b) => {
				const aTime = new Date(a.requestedAt).getTime() || 0;
				const bTime = new Date(b.requestedAt).getTime() || 0;
				return bTime - aTime;
			});
			const duplicateCount = etdData.length - mergedEtdData.length;
			const totalElements =
				(cancelRes?.data?.totalElements || 0) +
				Math.max((etdRes?.data?.totalElements || 0) - duplicateCount, mergedEtdData.length);
			const hasNext = cancelData.length === size || etdData.length === size;

			setState((prevState) => ({
				...prevState,
				projectList: merge
					? [...projectList, ...dropAlreadyListedRfqChangeOrders(combined, projectList)]
					: combined,
				page: page,
				hasNext: hasNext,
				totalElements: totalElements,
				loading: false
			}));
		} catch {
			setState((prevState) => ({ ...prevState, loading: false }));
			toastError('Something went wrong! Please try again.');
		}
	};

	const handleApprovalRequestTypeChange = async (requestType) => {
		setState((prevState) => ({ ...prevState, approvalRequestType: requestType }));
		await renderApprovalList(0, false, requestType);
	};

	const handleApprovalRequestStatusChange = async (requestStatus) => {
		setState((prevState) => ({ ...prevState, approvalRequestStatus: requestStatus }));
		await renderApprovalList(0, false, state.approvalRequestType, requestStatus);
	};

	const renderList = async (page = 0, activeTab, merge = true) => {
		if (!activeTab) return;

		if (activeTab === 'APPROVAL') {
			await renderApprovalList(page, merge);
			return;
		}

		setState((prevState) => ({ ...prevState, loading: true }));

		const { size, projectList } = state;
		let statusFilter_text = `&statusList=${tabFilterMap[activeTab]}`;
		statusFilter_text = generateTeamParams(myTeamMembers, statusFilter_text);
		let params = { page: page, size: size };
		let paramData = encodeQueryData(params);
		let orderQuery = generateOrderDashboardParams(orderDashboardFilterFields);
		if (orderQuery === '' || orderQuery.indexOf('sort=') === -1) {
			orderQuery += `&sort=${activeTab === 'ORDER_DASHBOARD' ? 'order.id,desc' : 'id,desc'}`;
		}

		let apiKey = activeTab === 'ORDER_DASHBOARD' ? 'getOrderDashboardReports' : 'getOrderList';

		setState((prevState) => ({ ...prevState, queryParams: `${orderQuery}${statusFilter_text}` }));
		try {
			let mergedParams = paramData + orderQuery + statusFilter_text;
			const urlParams = new URLSearchParams(mergedParams);
			if (activeTab !== 'ORDER_DASHBOARD') {
				urlParams.set('sort', 'id,desc');
			}
			const updatedQueryString = urlParams.toString();
			const { data } = await ApiClient.GET(apiKey, `?${updatedQueryString}`);
			if (data.data.length > 0) {
				setState((prevState) => ({
					...prevState,
					projectList: merge ? [...projectList, ...data.data] : data.data,
					page: page,
					hasNext: data.data.length === size,
					totalElements: data.totalElements,
					loading: false
				}));
			} else {
				setState((prevState) => ({
					...prevState,
					projectList: merge ? projectList : [],
					totalElements: data.totalElements,
					hasNext: false,
					loading: false
				}));
			}
		} catch {
			setState((prevState) => ({ ...prevState, loading: false }));
			toastError('Something went wrong! Please try again.');
		}
	};

	const renderFilters = () => {
		const { activeTab, showFilter } = state;
		return (
			<FilterComponentV2
				filterPageName={allFilterPages.MY_ORDER}
				fields={activeTab === 'ORDER_DASHBOARD' ? orderDashboardPageList : orderPageFieldList}
				showFilter={showFilter}
				isDependentFilter={true}
			/>
		);
	};

	const renderFilterTopHeader = () => {
		const { activeTab, totalElements, showFilter, showSummary } = state;
		return (
			<FilterTopHeader
				showFilter={showFilter}
				toggleFilter={() =>
					setState((prevState) => ({ ...prevState, showFilter: !prevState.showFilter }))
				}
				toggleSummary={async () => {
					const newShowSummary = !showSummary;
					setState((prevState) => ({ ...prevState, showSummary: newShowSummary }));
					const userPreference = getUserPreference();
					await setSystemUserPreferences({
						...userPreference,
						orderDashboardShowSummary: newShowSummary
					});
				}}
				showSummary={showSummary}
				totalItem={totalElements}
				isOrderDashboard={activeTab === 'ORDER_DASHBOARD'}
				showFilterButton={activeTab !== 'APPROVAL'}
				filterType={activeTab}
				approvalRequestType={state.approvalRequestType}
				onApprovalRequestTypeChange={handleApprovalRequestTypeChange}
				approvalRequestStatus={state.approvalRequestStatus}
				onApprovalRequestStatusChange={handleApprovalRequestStatusChange}
			/>
		);
	};

	const updateOrderStepByOrders = (orders) => {
		let modifiedProjectList = state.projectList?.map((item) => {
			let selectedOrder = orders.find((order) => order.id === item.id);
			if (selectedOrder) {
				item = selectedOrder;
			}
			return item;
		});
		setState((prev) => ({ ...prev, projectList: modifiedProjectList }));
	};

	const getOrderInfoByDashboardId = async (orderDashboardId) => {
		try {
			setState((prev) => ({ ...prev, loading: true }));
			const { data } = await getOrderByOrderDashboardId(`?rfqIdList=${orderDashboardId}`);
			if (data?.[0]) {
				let modifiedProjectList = state.projectList?.map((item) => {
					if (item.id === orderDashboardId) {
						item = data[0];
					}
					return item;
				});
				setState((prev) => ({ ...prev, projectList: modifiedProjectList }));
			}
		} catch (e) {
			printApiError(e);
		} finally {
			setState((prev) => ({ ...prev, loading: false }));
		}
	};

	const renderPageContents = () => {
		const { projectList, isPendingTab, activeTab, loading, showSummary, queryParams } = state;
		if (projectList?.length === 0 && !loading) {
			return <NoData message={'No order found'} />;
		} else if (activeTab === 'ORDER_DASHBOARD') {
			return (
				<OrderDashboard
					orderStore={projectList}
					handleOrderDashboardScroll={handleOrderDashboardScroll}
					callback={filter}
					showSummary={showSummary}
					queryParams={queryParams}
					getOrderInfoByDashboardId={getOrderInfoByDashboardId}
					updateOrderStepByOrders={updateOrderStepByOrders}
				/>
			);
		} else {
			return (
				<OrderLists orderStore={projectList} isStartFromHere={isPendingTab} activeTab={activeTab} />
			);
		}
	};

	const filter = () => {
		renderList(0, state.activeTab, false);
	};

	const generateTableClassName = () => {
		const { showFilter, activeTab } = state;
		let className = '';
		if (activeTab === 'ORDER_DASHBOARD' && showFilter) {
			className = 'data-table expand';
		} else if (activeTab === 'ORDER_DASHBOARD' && !showFilter) {
			className = 'data-table not-expand';
		} else {
			className = 'data-table';
		}
		return className;
	};
	const className =
		state.activeTab === 'ORDER_DASHBOARD'
			? 'filter-data-container filter-v3-data-container orders-data-container order-dashboard-container'
			: 'bg-white filter-data-container filter-v3-data-container clients-data-container bg-white py-0';

	const toggleAskModal = () => {
		setState((prev) => ({ ...prev, showAskModal: !prev.showAskModal }));
	};

	return (
		<LoaderComponent loading={state.loading}>
			<div className="top-header order-list-top-header">
				<div className="page-title-area bg-white d-flex justify-content-between align-items-center">
					<span className="semibold-16">Order list</span>
					<button
						className="ask-ai-icon button size32 ml-12 radius-2 d-none"
						onClick={toggleAskModal}
					>
						<span className="regular-14 weight-500 regular-14 text-white">Ask AI</span>
						<AiMagicIcon width={24} height={24} />
					</button>
				</div>
				<div className="tabs my-order-tabs bg-white">
					<MyOrderTabs
						runningCount={state.runningCount}
						shipmentCount={state.shipmentCount}
						pendingCount={state.pendingCount}
						completedCount={state.completedCount}
						cancelCount={state.cancelCount}
						activeTab={state.activeTab}
						changeTab={changeTab}
					/>
				</div>
				{renderFilterTopHeader()}
			</div>

			<section className={className}>
				{renderFilters()}
				<div className={generateTableClassName()}>{renderPageContents()}</div>
			</section>
			{state.showAskModal && <OrderReportingSideBar onClose={toggleAskModal} />}
		</LoaderComponent>
	);
};
export default MyOrder;
